import { useState, useRef, useCallback, useEffect } from "react";
import {
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
  Backdrop,
  Stack,
  Tooltip,
} from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import ConfirmationDialog from "../../../../Base/views/ConfirmationDialog";
import ReplayIcon from "@mui/icons-material/Replay";
import axiosInstance from "../../../../../../axiosInstance";
import { HRMS_ATS_API_URL } from "../../../../../Constants/baseConstant";
import { v4 as uuidv4 } from "uuid";

const VideoAssessment = ({
  videoQuestions,
  handleResponseSubmit,
  setCandidateAnswers,
  candidateAnswers,
  name,
  candidateData,
  candidateJobId,
}) => {
  const [startAssessment, setStartAssessment] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [prepTime, setPrepTime] = useState(60);
  const [isPrepping, setIsPrepping] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoKey, setVideoKey] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [videoBlob, setVideoBlob] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStage, setUploadStage] = useState("upload");

  const [interimTranscript, setInterimTranscript] = useState("");
  const transcriptRef = useRef("");
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const maxQuestions = candidateData?.prescreen_random_freq?.video_random_freq;
  const [assessmentVideoQuestions, setAssessmentVideoQuestions] = useState([]);
  const [retryCountMap, setRetryCountMap] = useState({});
  const recordingStartTimeRef = useRef(null);

  const handleStartAssessment = () => {
    const shuffled = shuffleArray(videoQuestions);
    const selected = shuffled.slice(0, maxQuestions);

    setAssessmentVideoQuestions(selected);
    setStartAssessment(true);
    setCurrentQuestionIndex(0);

    setTimeout(() => {
      handleStartPrep();
    },200)

  };
  const handleStartPrep = () => {
    // stop previous camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        setIsPrepping(true);
        setPrepTime(5);
      })
      .catch((error) => {
        console.error("Error accessing camera and microphone:", error);
        setUploadStatus("Failed to access camera and microphone");
      });
  };

  useEffect(() => {
    let interval;
    if (isPrepping && prepTime > 0) {
      interval = setInterval(() => {
        setPrepTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(interval);
            setIsPrepping(false);
            startRecording();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrepping, prepTime]);

  useEffect(() => {
    if (!isRecording) return;

    const totalTime = parseTime(
      assessmentVideoQuestions[currentQuestionIndex]?.time || "00:01:00",
    );

    setRemainingTime(totalTime);

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          stopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, currentQuestionIndex]);

  const parseTime = (timeString) => {
    const [hours, minutes, seconds] = timeString.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };
 
  const startRecording = useCallback(() => {
    transcriptRef.current = "";
    setInterimTranscript("");
    
    const stream = streamRef.current;
    if (!stream) {
      console.error("No stream available for recording");
      return;
    }
    recordingStartTimeRef.current = Date.now();
    streamRef.current = stream;
   
    videoRef.current.srcObject = stream;
    mediaRecorderRef.current = new MediaRecorder(stream);

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setVideoBlob(blob);
      chunksRef.current = [];
    };

    mediaRecorderRef.current.start();
    setIsRecording(true);
    isRecordingRef.current = true; 
    startSpeechRecognition();   // START TRANSCRIPT
     
  }, []);

  // supported in Google Chrome, Edge, Brave   || Not-supported in Firefox, Safari (partial)
  const startSpeechRecognition = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn("Speech Recognition not supported");
      return;
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      let interim = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;

        if (event.results[i].isFinal) {
          transcriptRef.current += text + " ";
        } else {
          interim += text;
        }
      }

      setInterimTranscript(interim);
    };

    recognition.onend = () => {
      if (isRecordingRef.current && recognitionRef.current) {
        try {
          recognition.start();
        } catch (e) {}
      }
    };

    recognition.onerror = (e) => {
      console.warn("Speech recognition error:", e);
    };

    try {
      recognition.start();
    } catch (e) {}

    recognitionRef.current = recognition;
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false; 

      // calculate recording time
      const recordingEndTime = Date.now();
      const recordedDuration = Math.floor(
        (recordingEndTime - recordingStartTimeRef.current) / 1000
      );

      const currentQuestionId =
        assessmentVideoQuestions[currentQuestionIndex]?.id;

      setRetryCountMap((prev) => ({
        ...prev,
        [currentQuestionId]: {
          ...(prev[currentQuestionId] || {}),
          duration: recordedDuration,
          retries: prev[currentQuestionId]?.retries || 0,
        },
      }));

      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  }, [isRecording, assessmentVideoQuestions, currentQuestionIndex]);

  useEffect(() => {
    if (videoBlob) {
      const url = URL.createObjectURL(videoBlob);
      setVideoUrl(url);

      setVideoKey((prevKey) => prevKey + 1);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [videoBlob]);

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleProceed = (isSkip) => {
    let newAnswer;
    if (isSkip === "YES") {
      newAnswer = {
        question: assessmentVideoQuestions[currentQuestionIndex]?.question,
        question_type: "video",
        id: assessmentVideoQuestions[currentQuestionIndex]?.id,
        file: "",
        transcript: "",
        answer: "Skipped"
      };
    } else if (videoBlob) {
      const file = new File(
        [videoBlob],
        `question_${currentQuestionIndex + 1}.webm`,
        { type: "video/webm" },
      );
      newAnswer = {
        question: assessmentVideoQuestions[currentQuestionIndex]?.question,
        question_type: "video",
        id: assessmentVideoQuestions[currentQuestionIndex]?.id,
        file: file,
        transcript: `${transcriptRef.current} ${interimTranscript}`.trim(),
        answer: `${candidateData?.domain_name}/ATS/VideoQuestions/${candidateJobId}/${uuidv4()}.mp4`,
      };

    }
    if (!newAnswer) return;
    const existingResponses = candidateAnswers?.video_response || [];

    const updatedResponses = [
      ...existingResponses.filter(
        (q) => q.id !== newAnswer.id
      ),
      newAnswer,
    ];
    setCandidateAnswers({ video_response: updatedResponses });

    transcriptRef.current = "";
    setInterimTranscript("");

    if (currentQuestionIndex === assessmentVideoQuestions?.length - 1) {
      setOpenConfirmDialog(true);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
      setVideoBlob(null);
      setVideoUrl(null);
      setIsRecording(false);
      isRecordingRef.current = false;

      setTimeout(() => {
        handleStartPrep()
      },200)
    }
  };
  const handleConfirmSubmit = async () => {
    setOpenConfirmDialog(false);
    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadStage("preparing");
    try {
      const formData = new FormData();
      
      candidateAnswers?.video_response?.forEach((item) => {
        if (item.file instanceof File && item.answer !== "Skipped") {
          formData.append("files", item?.file);
          formData.append("file_name", item?.answer);
        }
      });
      setUploadStage("upload");
      await axiosInstance.post(
        "/ats/ancillary/upload-video-answer",
        formData,
        {
          baseURL: HRMS_ATS_API_URL,
          headers: {
            "Content-Type": "multipart/form-data",
          },
           onUploadProgress: (event) => {
            if (!event.total) return;

            const percent = Math.round(
              (event.loaded * 100) / event.total
            );
            setUploadProgress(percent);
             if (percent === 100) {
                setUploadStage("processing");
              }
          },
        },
        
      );
      setUploadStage("processing");      
      setUploadStage("finalizing");
      setTimeout(() => {
        setIsSubmitting(false);
        setUploadProgress(0);
        handleResponseSubmit();
      }, 500);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus(`Upload failed: ${error.message}`);
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadStage(null);
    }
  };

  const handleTryAgain = () => {
    const currentQuestionId = assessmentVideoQuestions[currentQuestionIndex]?.id;

    setRetryCountMap((prev) => ({
      ...prev,
      [currentQuestionId]: {
        ...(prev[currentQuestionId] || {}),
        retries: (prev[currentQuestionId]?.retries || 0) + 1,
      },
    }));

    setVideoBlob(null);
    setVideoUrl(null);
    handleStartPrep();
  };
  const getRetryStatus = () => {
    const currentQuestionId = assessmentVideoQuestions[currentQuestionIndex]?.id;
    const questionTime = parseTime(
      assessmentVideoQuestions[currentQuestionIndex]?.time || "00:01:00"
    );
    const data = retryCountMap[currentQuestionId];

     if (!data) {
        return {
          allowed: true,
          message: "You can retry once",
        };
      }

    const { duration = 0, retries = 0 } = data;
    if (duration >= questionTime / 2) {
      return {
        allowed: false,
        message: "Not Allowed: You have used more than 50% of time",
      };
    }
     if (retries >= 1) {
      return {
        allowed: false,
        message: "Only one retry allowed",
      };
    }
     return {
      allowed: true,
      message: "You can retry once",
    };
  };
  const retryStatus = getRetryStatus();

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);
  const renderQuestionProgress = () => {
    const progress =
      ((currentQuestionIndex + 1) / assessmentVideoQuestions?.length) * 100;
    return (
      <Box>
        <Box>
          <Typography
            variant="subtitle4"
            color="text.secondary"
            fontWeight={600}
          >
            Question {currentQuestionIndex + 1} of {assessmentVideoQuestions?.length}
          </Typography>
        </Box>
        <Box sx={{ width: 120, mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            className="mpl-yellow-bg"
            sx={{
              height: { xs: 5, sm: 6, md: 8 },
              borderRadius: 5,
              bgcolor: "#F2F4F7",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
              },
            }}
          />
        </Box>
      </Box>
    );
  };
  return (
    <Box>
      <Box sx={{ width: "100%", maxWidth: "800px", margin: "auto" }}>
        <Box
          sx={{
            py: { xs: 2, md: 3 },
            px: { xs: 2, md: 5 },
          }}
        >
          {!startAssessment ? (
            <Stack spacing={2} sx={{ alignItems: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              ></Box>
              <Typography sx={{ fontSize: "14px" }}>Hey, {name}!</Typography>
              <Typography
                sx={{ fontSize: { xs: "18px", sm: "24px", md: "28px" } }}
                variant="h3"
                mt={4}
                mx={6}
              >
                Welcome to the Video Assessment
              </Typography>
              <Typography
                sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }}
                variant="body3"
              >
                What's next? You're about to start the video assessment. This
                will involve recording short video responses to a series of
                questions. Make sure you're in a quiet environment with good
                lighting and a stable internet connection.
              </Typography>
              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "18px", sm: "24px", md: "20px" },
                    mb: "0.5rem",
                  }}
                >
                  Before you begin
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    textAlign: "left",
                    paddingLeft: "20px",
                    fontSize: { xs: "10px", sm: "12px", md: "14px" },
                  }}
                >
                  <li style={{ marginBottom: "0.25rem" }}>
                    <Typography variant="body6" color="#475467">
                      Check that your camera and microphone are working
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "0.25rem" }}>
                    <Typography variant="body6" color="#475467">
                      Ensure you have a stable internet connection
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "0.25rem" }}>
                    <Typography variant="body6" color="#475467">
                      Find a quiet place where you won't be interrupted
                    </Typography>
                  </li>
                  <li style={{ marginBottom: "0.25rem" }}>
                    <Typography variant="body6" color="#475467">
                      Maintain eye contact with the camera and avoid looking away during the assessment
                    </Typography>
                  </li>
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={handleStartAssessment}
                className="ext1-mpl-btn"
                sx={{
                  py: 1,
                  fontSize: { xs: "12px", sm: "14px", md: "18px" },
                  width: "50%",
                  minWidth: 250,
                  borderRadius: "10px",
                  textTransform: "none",
                }}
              >
                Start Now
              </Button>
            </Stack>
          ) : (
            <Box>
              <Box
                display="flex"
                alignItems="start"
                justifyContent="center"
                mt={-1}
              >
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: 24, md: 32 },
                  }}
                >
                  Video Assessment
                </Typography>
              </Box>
              <Typography
                varian="h4"
                gutterBottom
                sx={{
                  mb: 1.5,
                  textAlign: "center",
                  fontSize: { xs: 14, sm: 18 },
                  fontWeight: { xs: 600, md: 700 },
                }}
              >
                {assessmentVideoQuestions[currentQuestionIndex]?.question}
              </Typography>
              <Box
                sx={{
                  mb: 2,
                  textAlign: "center",
                  position: "relative",
                  height: { xs: "40vh", md: "45vh" },
                  minHeight: 280,
                }}
              >
                {videoUrl ? (
                  <video
                    src={videoUrl}
                    key={videoKey}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "80%",
                      height: "100%",
                      backgroundColor: "black",
                      borderRadius: "20px",
                    }}
                    controls
                  />
                ) : (
                  <video
                    ref={videoRef}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "80%",
                      height: "100%",
                      backgroundColor: "black",
                      borderRadius: "20px",
                    }}
                    autoPlay
                    muted
                    playsInline
                  />
                )}
                {isPrepping && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={(prepTime / 5) * 100}
                      size={80}
                      sx={{
                        color: "white",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        fontSize: { xs: "16px", sm: "20px" },
                      }}
                    >
                      {formatTime(prepTime)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  position: "relative",
                }}
              >
                {isRecording || isPrepping ? (
                  <Button
                    onClick={stopRecording}
                    variant="outlined"
                    disabled={isPrepping}
                    startIcon={
                      <StopIcon
                        sx={{
                          color: "red",
                        }}
                      />
                    }
                    sx={{
                      width: "40%",
                      minWidth: 250,
                      color: "red",
                      py: 1,
                      fontSize: { xs: "12px", sm: "14px", md: "18px" },
                    }}
                  >
                    Finish Recording
                  </Button>
                ) : videoUrl ? (
                  <Stack
                    spacing={2}
                    direction={{ xs: "column", sm: "row", md: "row" }}
                    sx={{ alignItems: "center" }}
                  >
                    <Button
                      onClick={handleProceed}
                      variant="contained"
                      className="ext1-mpl-btn"
                      sx={{
                        p: 1,
                        fontSize: { xs: "12px", sm: "14px", md: "18px" },
                        width: "35%",
                        minWidth: 200,
                      }}
                    >
                      {currentQuestionIndex === assessmentVideoQuestions?.length - 1
                        ? "Submit"
                        : "Next"}
                    </Button>
                    <Tooltip title={!retryStatus.allowed ? retryStatus.message : "You can retry once"} arrow>
                      <span>
                        <Button
                          onClick={handleTryAgain}
                          disabled={!retryStatus.allowed}
                          sx={{
                            p: 1,
                            fontSize: { xs: "12px", sm: "14px", md: "18px" },
                            width: "35%",
                            minWidth: 200,
                          }}
                          variant="outlined"
                          startIcon={<ReplayIcon />}
                        >
                          Try Again
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  // <Button
                  //   onClick={handleStartPrep}
                  //   variant="contained"
                  //   sx={{
                  //     p: 1,
                  //     fontSize: { xs: "12px", sm: "14px", md: "18px" },
                  //     width: "35%",
                  //     minWidth: 200,
                  //   }}
                  //   className="ext1-mpl-btn"
                  //   startIcon={<RadioButtonCheckedIcon />}
                  // >
                  //   Record Response
                  // </Button>
                  <Typography
                    variant="subtitle4"
                    color="text.secondary"
                  >
                    Recording will start automatically
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
                  {renderQuestionProgress()}

                  <Button 
                    onClick={() => handleProceed("YES")} 
                    variant="text"
                    disabled={isRecording}
                    sx={{
                      fontSize: { xs: "12px", sm: "14px", md: "18px" },
                    }}
                    >
                      Skip
                  </Button>
                </Stack>
                <Box>
                  {isRecording && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle4"
                        color="text.secondary"
                        fontWeight={600}
                      >
                        Remaining Time:{" "}
                      </Typography>
                      <Typography variant="h6" className="mpl-yellow-color">
                        {formatTime(remainingTime)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              {uploadStatus && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {uploadStatus}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmationDialog
        title="Confirm Submission"
        content={"Are you sure you want to submit all your recorded responses?"}
        openDialog={openConfirmDialog}
        closeDialog={setOpenConfirmDialog}
        OkButtonText="Yes"
        onSubmit={handleConfirmSubmit}
      />

      {isSubmitting && <UploadProgressOverlay progress={uploadProgress} stage={uploadStage}/>}
    </Box>
  );
};


const UploadProgressOverlay = ({ progress, stage }) => {
  const getMessage = () => {
    switch (stage) {
      case "upload":
        return `Uploading videos... ${progress}%`;
      case "processing":
        return "Processing videos. Please wait...";
      case "finalizing":
        return "Finalizing submission...";
      default:
        return "Submitting assessment...";
    }
  };

  return (
    <Backdrop open sx={{
      backgroundColor: "rgba(0,0,0,0.8)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <Box sx={{
        width: "90%",
        maxWidth: 420,
        bgcolor: "white",
        borderRadius: "12px",
        p: 4,
        textAlign: "center"
      }}>
        <Typography variant="h6" mb={3}>
          Submitting Assessment
        </Typography>

        {stage === "upload" ? (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5 }}
          />
        ) : (
          <CircularProgress />
        )}

        <Typography mt={2}>
          {getMessage()}
        </Typography>
      </Box>
    </Backdrop>
  );
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export default VideoAssessment;
